import { useMemo } from "react";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { useIsReviewItemListed } from "@components/QaReview/hooks/useReviewView";

/**
 * The cards the review bar walks and the advance after Resolve steps through: every card the current view
 * lists, in the report order the panel publishes. The view decides which cards; the order only sorts them, so
 * a card the order doesn't have yet (right after a view switch, or when Quality Review lands new flags) goes
 * last instead of going missing until the panel republishes.
 */
export const useNavigationItems = (): { items: ReviewItemFieldsFragment[]; isPending: boolean } => {
  const { resolvableQaItems, rootComments } = useReviewData();
  const { state: session } = useReviewSession();
  const isListed = useIsReviewItemListed();

  const listed = useMemo(
    () => [...resolvableQaItems, ...rootComments].filter(isListed),
    [resolvableQaItems, rootComments, isListed],
  );
  const items = useMemo(() => {
    const rank = new Map(session.navigationOrder.map((id, index) => [id, index]));
    const rankOf = (item: ReviewItemFieldsFragment) => rank.get(item.id) ?? Number.MAX_SAFE_INTEGER;
    // Array sort is stable, so cards the order doesn't have keep their feed order at the end.
    return [...listed].sort((a, b) => rankOf(a) - rankOf(b));
  }, [listed, session.navigationOrder]);

  // The panel hasn't measured and published its cards yet, so the order is unknown: show no position.
  return { items, isPending: !session.isNavigationOrderPublished && listed.length > 0 };
};
